import _, { get } from "lodash";

import { NO_GROUPING_QS_VALUE, rideTableLayoutInitial } from "../constants";
import { IExtendableDto } from "../dto";
import {
  ProduceButtonsPageViewMode,
  StarredDisplayModeEnum,
  UserEditDisplayModeEnum,
} from "../enums";
import { IFilterSchemaObject, RydeUiUnit, ValueType } from "../models";
import {
  buildPartsLayoutSignature,
  LAYOUT_SIGNATURE_EMPTY,
} from "./layout-signature.helpers";
import {
  ColumnOrderState,
  ExpandedStateList,
  GroupingState,
  PaginationState,
  SortingState,
  VisibilityState,
} from "./react-table.features";

export const getConstInitialColumnsVisibilityState = (
  entity: RydeUiUnit,
): VisibilityState => {
  switch (entity) {
    case "ride":
      return rideTableLayoutInitial.columnVisibility;
    default:
      return {};
  }
};

export const getConstInitialColumnsOrderState = (
  entity: RydeUiUnit,
): ColumnOrderState => {
  switch (entity) {
    default:
      return [];
  }
};

export const getConstInitialColumnsSortingState = (
  entity: RydeUiUnit,
): SortingState => {
  switch (entity) {
    default:
      return [];
  }
};

export const getConstInitialColumnsGroupingState = (
  _entity: RydeUiUnit,
): GroupingState => {
  return [];
};

/** Bust react-window row memo when rows move between groups without group key / total length changes. */
export const buildGroupedTableRowsLayoutSignature = ({
  tableRows,
  groupingField,
}: {
  tableRows: IExtendableDto[] | undefined;
  groupingField: string | number | undefined;
}): string => {
  if (!tableRows?.length) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  if (groupingField === undefined || groupingField === NO_GROUPING_QS_VALUE) {
    return String(tableRows.length);
  }

  const groupingFieldName = String(groupingField);

  const parts = tableRows.map(
    row =>
      `${row.id}:${String(get(row as Record<string, unknown>, groupingFieldName))}`,
  );

  return buildPartsLayoutSignature(parts, { sort: true });
};

/** Ordered group keys for grouped table List rowProps memo. */
export const buildGroupValuesLayoutSignature = (
  groupValues: readonly (string | number | undefined)[],
): string =>
  buildPartsLayoutSignature(
    groupValues.map(groupValue => String(groupValue ?? "")),
  );

export const buildTableColumnDefsLayoutSignature = (
  columnDefs: readonly { id?: string }[],
): string =>
  buildPartsLayoutSignature(columnDefs.map(column => String(column.id ?? "")));

export const buildColumnVisibilityLayoutSignature = (
  columnVisibility: VisibilityState | undefined,
): string => {
  if (!columnVisibility) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  const parts = Object.entries(columnVisibility)
    .sort(([columnIdA], [columnIdB]) => columnIdA.localeCompare(columnIdB))
    .map(([columnId, isVisible]) => `${columnId}:${String(isVisible)}`);

  return buildPartsLayoutSignature(parts);
};

export const buildPaginationLayoutSignature = (
  pagination: PaginationState | null | undefined,
): string => {
  if (!pagination) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  return `${pagination.pageIndex}:${pagination.pageSize}`;
};

export const buildGridExpandedLayoutSignature = (
  expanded: ExpandedStateList | undefined,
): string => {
  if (!expanded || Object.keys(expanded).length === 0) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  const parts = Object.entries(expanded)
    .sort(([rowIdA], [rowIdB]) => rowIdA.localeCompare(rowIdB))
    .map(([rowId, isExpanded]) => `${rowId}:${String(isExpanded)}`);

  return buildPartsLayoutSignature(parts);
};

export const buildIdListLayoutSignature = (
  ids: readonly number[] | undefined,
): string => {
  if (!ids?.length) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  return buildPartsLayoutSignature(
    ids.map(id => String(id)),
    { sort: true },
  );
};

const serializeFilterValueForLayoutSignature = (val: ValueType): string => {
  if (Array.isArray(val)) {
    return `~${val.join(",")}`;
  }

  return String(val);
};

export const buildFiltersSchemaLayoutSignature = (
  filterSchema: IFilterSchemaObject | undefined,
): string => {
  if (!filterSchema) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  const parts = Object.entries(filterSchema)
    .filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !_.isEmpty(value),
    )
    .sort(([fieldIdA], [fieldIdB]) => fieldIdA.localeCompare(fieldIdB))
    .map(
      ([fieldId, value]) =>
        `${fieldId}:${serializeFilterValueForLayoutSignature(value as ValueType)}`,
    );

  if (!parts.length) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  return buildPartsLayoutSignature(parts);
};

export const buildJsonValueLayoutSignature = (value: unknown): string => {
  if (value === undefined || value === null) {
    return LAYOUT_SIGNATURE_EMPTY;
  }

  return buildPartsLayoutSignature([JSON.stringify(value)]);
};

export interface IGroupedTableRowRemeasureSignatureInput {
  layoutSignature: string;
  tableRowsLength: number | undefined;
  groupingValue: string | number | undefined | null;
  collapsed: boolean;
  nearbyFilteringRideId: number | null | undefined;
  nearByFilterIsActive: boolean;
  isDraggingBetweenRides: number | undefined;
  starredOnly: StarredDisplayModeEnum;
  userEditedMode: UserEditDisplayModeEnum;
  produceButtonPageView: ProduceButtonsPageViewMode;
  searchBoxText: string | undefined;
  activeViewId: number | undefined;
  showSelectedOnlyRowsFilterIsActive: boolean;
  tableItemPagination: PaginationState | null;
  gridExpandedCollapse: ExpandedStateList;
  filterCriteria: unknown;
  filtersSchema: IFilterSchemaObject | undefined;
  starredIds: readonly number[] | undefined;
}

/** Single memo key for grouped react-window row height remeasurement. */
export const buildGroupedTableRowRemeasureSignature = ({
  layoutSignature,
  tableRowsLength,
  groupingValue,
  collapsed,
  nearbyFilteringRideId,
  nearByFilterIsActive,
  isDraggingBetweenRides,
  starredOnly,
  userEditedMode,
  produceButtonPageView,
  searchBoxText,
  activeViewId,
  showSelectedOnlyRowsFilterIsActive,
  tableItemPagination,
  gridExpandedCollapse,
  filterCriteria,
  filtersSchema,
  starredIds,
}: IGroupedTableRowRemeasureSignatureInput): string =>
  buildPartsLayoutSignature([
    layoutSignature,
    String(tableRowsLength ?? 0),
    String(groupingValue ?? ""),
    String(collapsed),
    String(nearbyFilteringRideId ?? ""),
    String(nearByFilterIsActive),
    String(isDraggingBetweenRides ?? ""),
    String(starredOnly),
    String(userEditedMode),
    String(produceButtonPageView ?? ""),
    searchBoxText ?? "",
    String(activeViewId ?? ""),
    String(showSelectedOnlyRowsFilterIsActive),
    buildPaginationLayoutSignature(tableItemPagination),
    buildGridExpandedLayoutSignature(gridExpandedCollapse),
    buildJsonValueLayoutSignature(filterCriteria),
    buildFiltersSchemaLayoutSignature(filtersSchema),
    buildIdListLayoutSignature(starredIds),
  ]);
